import { sanitizeUrl } from '@overlayqa/shared';
import { FigmaEmbed } from '../../components/figma';
import type { Ticket } from '@overlayqa/shared';
import { log } from '@/lib/logger';

interface FigmaEmbedPanelProps {
  ticket: Ticket;
  ticketId: string;
}

/**
 * Figma panel for an issue whose project has a linked file: the embed plus a
 * direct link out to Figma. Both are TOKENLESS by design — Figma authorizes
 * the viewer against their own Figma session, so an invited teammate who has
 * never connected Figma to OverlayQA sees the design and nothing fails.
 *
 * It previously also fetched the file's page list with the VIEWER's token to
 * offer a page picker. The file link is a PROJECT fact and the token is a
 * PER-USER one, so for a teammate that request could only ever answer 400
 * NOT_CONNECTED — and its error had nowhere to surface, because the drawer
 * that rendered it opened from a button gated on the data the failed fetch
 * never returned. Deleted 2026-09-01; the picker only overrode the node id the
 * ticket already carries.
 */
export function FigmaEmbedPanel({ ticket, ticketId }: FigmaEmbedPanelProps) {
  log.info('[FigmaEmbedPanel] Rendering for ticket:', { ticketId });

  return (
    <div className="space-y-6">
      {/* Figma Embed */}
      <section id="figma-embed" className="card-sm">
        <h2 className="section-label mb-3 flex items-center justify-between">
          Figma Design
          <a
            href={sanitizeUrl(
              ticket.figmaFrameUrl
                || `https://www.figma.com/design/${ticket.figmaFileKey}${ticket.figmaNodeId ? `?node-id=${ticket.figmaNodeId.replace(/:/g, '-')}&mode=design` : ''}`
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary-500 hover:underline"
            style={{ fontSize: '.625rem' }}
          >
            <span className="text-cap-trim block">Open in Figma</span>
          </a>
        </h2>
        <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mt-4">
          <FigmaEmbed
            fileKey={ticket.figmaFileKey!}
            nodeId={ticket.figmaNodeId}
            className="w-full h-full"
          />
        </div>
      </section>
    </div>
  );
}
